import Blockly from "blockly";
import { javascriptGenerator, Order } from "blockly/javascript";
import { KMainFunctions } from "../shared/utility/main-functions";

const K_PARAMETER_BLOCK_NAMES = [
  "kparameter_string",
  "kparameter_boolean",
  "kparameter_int16",
  "kparameter_int32",
  "kparameter_int64",
  "kparameter_decimal",
  "kparameter_double",
  "kparameter_datetime"
];

const validateParamName = (_currentBlock: Blockly.Block, value: string, currentBlockType: string) => {
  // Validate parameter name format
  if (!value.match(/^[a-zA-Z_$][a-zA-Z0-9_$]*$/)) {
    return null;
  }
  // Get the current workspace
  const workspace = Blockly.getMainWorkspace();

  // Iterate through all blocks in the workspace
  const blocks = workspace.getAllBlocks();
  const isUnique = blocks.every((block) => {
    if (K_PARAMETER_BLOCK_NAMES.includes(block.type)) return true;

    if (block.type === currentBlockType) return true;

    const blockValue = block.getFieldValue("paramName");
    return blockValue !== value;
  });

  return isUnique ? value : null;
};

const initializeKMainFunction = () => {
  javascriptGenerator.forBlock.kfunction_main = function (block, generator) {
    const functionBody = generator.statementToCode(block, "kfuntion_main_body");
    const returnStatement = generator.valueToCode(block, "kfuntion_main_return", Order.ATOMIC);

    const code = `\nfunction kFunctionMain () {\n\t${functionBody}\n\treturn ${returnStatement};\n}`;
    return code;
  };
};
const initializeKParameters = (type: string) => {
  javascriptGenerator.forBlock[type] = (block, _generator) => {
    const text_paramname = block.getFieldValue("paramName");
    if (!text_paramname) {
      KMainFunctions.displayWarningNotification("Parameter Name is required");
      throw new Error("Parameter Name is required");
    }
    if (!validateParamName(block, text_paramname, type)) {
      KMainFunctions.displayWarningNotification("Different parameter types cannot have same Parameter Name");
      throw new Error("Different parameter types cannot have same Parameter Name");
    }
    const code = `@${text_paramname}`.replaceAll(" ", "_");
    return [code, Order.ATOMIC];
  };
};

Blockly.Blocks.kparameter_string = {
  init() {
    const block = this;

    const paramNameField = new Blockly.FieldTextInput("parameter1");
    paramNameField.setValidator((value) => validateParamName(block, value, "kparameter_string"));

    this.appendDummyInput().appendField("String Parameter Name").appendField(paramNameField, "paramName");
    this.setOutput(true, "String");
    this.setColour(180);
    this.setTooltip("String Parameter");
    this.setHelpUrl("https://kuika.com");
  }
};

Blockly.Blocks.kparameter_boolean = {
  init() {
    const block = this;

    const paramNameField = new Blockly.FieldTextInput("parameter1");
    paramNameField.setValidator((value) => validateParamName(block, value, "kparameter_boolean"));

    this.appendDummyInput().appendField("Boolean Parameter Name").appendField(paramNameField, "paramName");
    this.setOutput(true, "Boolean");
    this.setColour(180);
    this.setTooltip("Boolean Parameter");
    this.setHelpUrl("https://kuika.com");
  }
};

Blockly.Blocks.kparameter_int16 = {
  init() {
    const block = this;

    const paramNameField = new Blockly.FieldTextInput("parameter1");
    paramNameField.setValidator((value) => validateParamName(block, value, "kparameter_int16"));

    this.appendDummyInput().appendField("Int16 Parameter Name").appendField(paramNameField, "paramName");
    this.setOutput(true, "Number");
    this.setColour(180);
    this.setTooltip("Int16 Parameter");
    this.setHelpUrl("https://kuika.com");
  }
};

Blockly.Blocks.kparameter_int32 = {
  init() {
    const block = this;

    const paramNameField = new Blockly.FieldTextInput("parameter1");
    paramNameField.setValidator((value) => validateParamName(block, value, "kparameter_int32"));

    this.appendDummyInput().appendField("Int32 Parameter Name").appendField(paramNameField, "paramName");
    this.setOutput(true, "Number");
    this.setColour(180);
    this.setTooltip("Int32 Parameter");
    this.setHelpUrl("https://kuika.com");
  }
};

Blockly.Blocks.kparameter_int64 = {
  init() {
    const block = this;

    const paramNameField = new Blockly.FieldTextInput("parameter1");
    paramNameField.setValidator((value) => validateParamName(block, value, "kparameter_int64"));

    this.appendDummyInput().appendField("Int64 Parameter Name").appendField(paramNameField, "paramName");
    this.setOutput(true, "Number");
    this.setColour(180);
    this.setTooltip("Int64 Parameter");
    this.setHelpUrl("https://kuika.com");
  }
};

Blockly.Blocks.kparameter_decimal = {
  init() {
    const block = this;

    const paramNameField = new Blockly.FieldTextInput("parameter1");
    paramNameField.setValidator((value) => validateParamName(block, value, "kparameter_decimal"));

    this.appendDummyInput().appendField("Decimal Parameter Name").appendField(paramNameField, "paramName");
    this.setOutput(true, "Number");
    this.setColour(180);
    this.setTooltip("Decimal Parameter");
    this.setHelpUrl("https://kuika.com");
  }
};

Blockly.Blocks.kparameter_double = {
  init() {
    const block = this;

    const paramNameField = new Blockly.FieldTextInput("parameter1");
    paramNameField.setValidator((value) => validateParamName(block, value, "kparameter_double"));

    this.appendDummyInput().appendField("Double Parameter Name").appendField(paramNameField, "paramName");
    this.setOutput(true, "Number");
    this.setColour(180);
    this.setTooltip("Double Parameter");
    this.setHelpUrl("https://kuika.com");
  }
};

Blockly.Blocks.kparameter_datetime = {
  init() {
    const block = this;

    const paramNameField = new Blockly.FieldTextInput("parameter1");
    paramNameField.setValidator((value) => validateParamName(block, value, "kparameter_datetime"));

    this.appendDummyInput().appendField("Datetime Parameter Name").appendField(paramNameField, "paramName");
    this.setOutput(true, "DateTime");
    this.setColour(180);
    this.setTooltip("DateTime Parameter");
    this.setHelpUrl("https://kuika.com");
  }
};
// Initialize "Main Function" Block
Blockly.Blocks.kfunction_main = {
  init() {
    this.appendDummyInput().appendField("Main Function");
    this.appendStatementInput("kfuntion_main_body").setCheck(null);
    this.appendValueInput("kfuntion_main_return")
      .setCheck(null)
      .setAlign(Blockly.inputs.Align.RIGHT)
      .appendField("return");
    this.setColour(230);
    this.setTooltip("");
    this.setHelpUrl("");
    this.setDeletable(false);
  }
};

// Insert connection type for "kfunction_main" block
Blockly.Blocks.procedures_ifreturn.FUNCTION_TYPES.push("kfunction_main");

K_PARAMETER_BLOCK_NAMES.forEach((blockType) => {
  initializeKParameters(blockType);
});

initializeKMainFunction();
